import { Controller } from "@hotwired/stimulus"

// The floating "add comment" card. Mounted on the card itself (which the canvas
// controller clones out of a server-rendered template and appends to <body>), so
// every data-action inside it resolves even though it lives outside the canvas.
//
// The body is a contenteditable rather than a textarea because mention tokens have
// to render inline as styled, atomically-deletable spans.
export default class extends Controller {
  static targets = ["body", "submit", "attachments", "fileInput", "attachmentChipTemplate", "error"]
  static values = {
    createUrl: String,
    attachmentUrl: String,
    courseId: String,
    lessonContentId: String,
    // A section anchor (basics / objectives / course_test / evaluation /
    // start_screen) is posted instead of a lesson_content_id; exactly one is set.
    section: String,
    childKey: String,
    parentId: String,
    placeholder: String
  }

  connect() {
    this.attachments = []
    this.bodyTarget.dataset.placeholder = this.placeholderValue
    this.syncSubmitState()
    this.bodyTarget.focus()
  }

  onInput() {
    this.hideError()
    this.syncSubmitState()
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      // The typeahead consumes Escape first; if it is open the composer stays. Its
      // panel is a body-level singleton, so it is not found inside this element.
      if (document.querySelector(".course-review-mentions")) return

      event.preventDefault()
      event.stopPropagation()
      this.cancel()
      return
    }

    // Plain Enter inserts a newline; the modifier submits.
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      this.submit()
    }
  }

  cancel(event) {
    event?.preventDefault()

    // A hosted reply collapses back into its thread — restoring the Resolve/Reply/Dismiss
    // bar — rather than being torn down like the floating top-level composer.
    if (this.parentIdValue) {
      this.resetBody()
      window.dispatchEvent(new CustomEvent("course-review:reply-cancelled"))
      return
    }

    this.dismiss({ returnFocus: true })
  }

  dismiss({ returnFocus = false } = {}) {
    window.dispatchEvent(new CustomEvent("course-review:dismissed", { detail: { returnFocus } }))
  }

  // Return the composer to its empty state so the next Reply opens fresh.
  resetBody() {
    this.bodyTarget.innerHTML = ""
    this.attachments = []
    if (this.hasAttachmentsTarget) {
      this.attachmentsTarget.innerHTML = ""
      this.attachmentsTarget.hidden = true
    }
    this.hideError()
    this.syncSubmitState()
  }

  // ---- Body serialization ---------------------------------------------------

  // Mention spans serialize back to the `@[Display Name](user_id)` token the server
  // parses; everything else is plain text.
  serializedBody() {
    const parts = []

    this.bodyTarget.childNodes.forEach((node) => parts.push(this.serializeNode(node)))

    return parts.join("").trim()
  }

  serializeNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent

    if (node.nodeType !== Node.ELEMENT_NODE) return ""
    if (node.classList.contains("course-review-mention")) {
      const label = node.textContent.replace(/^@/, "")

      return `@[${label}](${node.dataset.userId})`
    }
    if (node.tagName === "BR") return "\n"

    return Array.from(node.childNodes).map((child) => this.serializeNode(child)).join("")
  }

  hasContent() {
    return this.serializedBody().length > 0
  }

  syncSubmitState() {
    const enabled = this.hasContent()

    this.submitTarget.disabled = !enabled
    this.submitTarget.classList.toggle("is-enabled", enabled)
  }

  onMentionSelected() {
    this.syncSubmitState()
  }

  // ---- Attachments ----------------------------------------------------------

  pickFile(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  async uploadFile(event) {
    const file = event.target.files?.[0]
    if (!file) return

    const payload = new FormData()
    // course_id lets the controller resolve the snapshot under review; without it
    // ensure_reviewing sees no reviewer and 403s the upload.
    payload.append("course_id", this.courseIdValue)
    payload.append("file", file)

    const response = await this.post(this.attachmentUrlValue, payload)
    event.target.value = ""
    if (!response?.success) return

    this.attachments.push(response.file)
    this.renderAttachmentChip(response.file)
  }

  renderAttachmentChip(file) {
    const chip = this.attachmentChipTemplateTarget.content.firstElementChild.cloneNode(true)

    if (file.image && file.url) {
      const thumb = chip.querySelector(".course-review-attachment__thumb")
      thumb.src = file.url
      thumb.alt = file.filename
      thumb.hidden = false
      chip.querySelector(".course-review-attachment__icon").hidden = true
      chip.classList.add("course-review-attachment--image")
    }

    chip.querySelector(".course-review-attachment__name").textContent = file.filename
    chip.dataset.signedId = file.signed_id
    this.attachmentsTarget.appendChild(chip)
    this.attachmentsTarget.hidden = false
  }

  removeAttachment(event) {
    event.preventDefault()

    const chip = event.currentTarget.closest(".course-review-attachment")

    this.attachments = this.attachments.filter((file) => file.signed_id !== chip.dataset.signedId)
    chip.remove()
    this.attachmentsTarget.hidden = this.attachments.length === 0
  }

  // ---- Submit ---------------------------------------------------------------

  async submit(event) {
    event?.preventDefault()
    if (!this.hasContent() || this.submitting) return

    this.submitting = true
    this.submitTarget.disabled = true
    this.hideError()

    const payload = new FormData()
    payload.append("course_id", this.courseIdValue)
    payload.append("comment[lesson_content_id]", this.lessonContentIdValue)
    payload.append("comment[section]", this.sectionValue)
    payload.append("comment[child_key]", this.childKeyValue)
    payload.append("comment[parent_id]", this.parentIdValue)
    payload.append("comment[body]", this.serializedBody())
    this.attachments.forEach((file) => payload.append("comment[file_signed_ids][]", file.signed_id))

    const response = await this.post(this.createUrlValue, payload)
    this.submitting = false

    if (!response?.success) {
      this.submitTarget.disabled = false
      this.showError(response?.errors?.[0] || "Your comment couldn't be added. Please try again.")
      return
    }

    window.dispatchEvent(new CustomEvent("course-review:created", {
      detail: { anchorKey: response.comment.anchor_key, comment: response.comment }
    }))
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }

  async post(url, payload) {
    try {
      const response = await fetch(url, {
        method: "POST",
        body: payload,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json"
        }
      })

      return await response.json()
    } catch (_error) {
      return null
    }
  }
}
